import { Button } from "@/src/components/tailwind/Button";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import {
  useQuestionQuery,
  useUpdateQuestionMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useRef, useEffect, useState } from "react";
import { H3 } from "@/src/components/Heading";
import { useQueryClient } from "@tanstack/react-query";
import { Option } from "@/src/components/tailwind/Dropdown";
import { CloseIcon } from "@/src/components/CloseIcon";
import { TextArea } from "@/src/components/tailwind/TextArea";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { useParams } from "react-router-dom";

export function UpdateQuestionModal(props: {
  open: boolean;
  onClose: () => void;
  questionId: string;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [details, setDetails] = useState("");
  const [selectedDfg, setSelectedDfg] = useState<Option | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");

  const updateButtonRef = useRef<HTMLButtonElement>(null);

  const client = useGqlClient();
  const queryClient = useQueryClient();

  // Fetch the question data
  const questionQuery = useQuestionQuery(
    client,
    {
      id: props.questionId,
    },
    {
      enabled: props.open && !!props.questionId,
    },
  );

  // Handle initial load of question data
  useEffect(() => {
    if (questionQuery.data && initialLoad) {
      setTitle(questionQuery.data.question.title);
      setDetails(questionQuery.data.question.body);

      if (questionQuery.data.question.forDealGroup) {
        setSelectedDfg({
          label: questionQuery.data.question.forDealGroup.name,
          value: questionQuery.data.question.forDealGroup.id,
        });
      } else {
        setSelectedDfg(null);
      }

      setInitialLoad(false);
    }
  }, [questionQuery.data, initialLoad]);

  const updateQuestion = useUpdateQuestionMutation(client);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (updateButtonRef.current) {
          updateButtonRef.current.click();
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Reset the form when the modal is opened or questionId changes
  useEffect(() => {
    if (props.open) {
      setInitialLoad(true);
      setTitleError("");
      setError("");
    }
  }, [props.open, props.questionId]);

  function resetFormToInitial() {
    if (questionQuery.data) {
      setTitle(questionQuery.data.question.title);
      setDetails(questionQuery.data.question.body);

      if (questionQuery.data.question.forDealGroup) {
        setSelectedDfg({
          label: questionQuery.data.question.forDealGroup.name,
          value: questionQuery.data.question.forDealGroup.id,
        });
      } else {
        setSelectedDfg(null);
      }
    }

    setTitleError("");
    setError("");
  }

  if (questionQuery.isLoading) {
    return (
      <AnimatedModal
        size="lg"
        position="center"
        open={props.open}
        onClose={props.onClose}
      >
        <div>
          <div className="justify-between flex items-center">
            <H3>Update question</H3>
            <CloseIcon onClose={props.onClose} />
          </div>
          <div className="mt-4">Loading question details...</div>
        </div>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal
      size="lg"
      position="center"
      open={props.open}
      onClose={() => {
        resetFormToInitial();
        props.onClose();
      }}
    >
      <div>
        <div className="justify-between flex items-center">
          <H3>Update question</H3>
          <CloseIcon onClose={props.onClose} />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (!title) {
              setTitleError("Title is required");
              return;
            }

            let questionForDealFirmGroupId = selectedDfg?.value;
            let removeQuestionForDealGroup = false;

            // Check if the selected DFG changed from what was originally set
            if (questionQuery.data?.question.forDealGroup) {
              if (!selectedDfg) {
                // If we had a forDealGroup before but now don't, we need to remove it
                removeQuestionForDealGroup = true;
              }
            }

            updateQuestion.mutate(
              {
                input: {
                  id: props.questionId,
                  title,
                  body: details,
                  questionForDealGroupID: questionForDealFirmGroupId,
                  removeQuestionForDealGroup,
                },
              },
              {
                onSuccess: () => {
                  // Invalidate queries to refresh the data
                  queryClient.invalidateQueries({
                    queryKey: ["Questions", { input: {} }],
                  });

                  queryClient.invalidateQueries({
                    queryKey: ["Question", { id: props.questionId }],
                  });

                  if (questionQuery.data?.question.ddql?.id) {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "Ddql",
                        { id: questionQuery.data.question.ddql.id },
                      ],
                    });
                  }

                  if (selectedDfg?.value) {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "DealFirmGroupQuestions",
                        { id: selectedDfg.value },
                      ],
                    });
                  }

                  props.onClose();
                },
                onError: (e) => {
                  setError("Failed to update question");
                },
              },
            );
          }}
        >
          <div className="mt-4">
            <TextInput
              label="Title"
              placeholder="Title"
              value={title}
              error={titleError}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
                setTitleError("");
              }}
            />

            <label
              htmlFor="details"
              className="block mt-3 text-sm font-medium leading-6 text-gray-900"
            >
              Details
            </label>
            <TextArea
              rows={6}
              name="details"
              value={details}
              onChange={(e) => {
                setDetails(e.currentTarget.value);
              }}
              placeholder="Any extra context/detail..."
            />

            {/* Deal firm group selection would go here - commented out for now
            Similar to what's commented out in the NewQuestionModal */}

            <div className="flex justify-between mt-6 z-0">
              <Button
                text="Cancel"
                variant="neutral"
                type="button"
                onClick={() => {
                  resetFormToInitial();
                  props.onClose();
                }}
              />
              <Button
                ref={updateButtonRef}
                isLoading={updateQuestion.isPending}
                loadingText="Updating..."
                text="Update question"
                variant="positive"
                type="submit"
              />
            </div>
            {error ? <div className="mt-4 text-red-500">{error}</div> : null}
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}
